import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { FaTrash } from 'react-icons/fa';
import { MultiSelect } from 'react-multi-select-component';
import { Heading, Input, Table } from '~/components';
import 'react-quill/dist/quill.snow.css';
import { TextEditor } from '~/components/TextEditor/TextEditor';
import { FileUploader } from '~/components/FileInput';
import { SelectItem } from '~/types';
import { MultiSelectStrings } from '~/utils/multiSelectStrings';
import {
  crateAnnouncement,
  getAnnouncementsById,
  updateAnnouncement,
} from '~/services/Announcements';
import { queryClient } from '~/utils/react-query';
import { AnnouncementsFile } from '~/types/announcements';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { getGroupsAsSelect } from '~/hooks';
import { getInternalRolesWithoutAdministratorOrClient } from '~/services/Roles';

export const NewAnnouncements: React.VFC = () => {
  const history = useHistory();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const { id } = useParams<{ id: string }>();
  const [isFetching, setIsFetching] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [listaArquivos, setListaArquivos] = useState<AnnouncementsFile[]>([]);
  const [gruposSelecionados, setGruposSelecionados] = useState<SelectItem[]>(
    [],
  );
  const [gruposUsuariosSelecionados, setGruposUsuariosSelecionados] = useState<
    SelectItem[]
  >([]);
  const [groups, setGroups] = useState<SelectItem[]>([]);
  const [internalRoles, setInternalRoles] = useState<SelectItem[]>([]);
  const [exigirCiencia, setExigirCiencia] = useState(false);
  const [altaPrioridade, setAltaPrioridade] = useState(false);
  const toast = useToast();

  useEffect(() => {
    getGroupsAsSelect(9999).then(_groups => {
      setGroups(_groups);
      getInternalRolesWithoutAdministratorOrClient(9999).then(
        _internalRoles => {
          setInternalRoles(_internalRoles);
          if (id) {
            setIsFetching(true);
            getAnnouncementsById(id)
              .then(response => {
                setTitulo(response.titulo);
                setMensagem(response.mensagem);
                setAltaPrioridade(response.alta_prioridade);
                setExigirCiencia(response.exigir_ciencia);
                setGruposSelecionados(
                  _groups.filter(item =>
                    response.grupo_clientes.includes(Number(item.value)),
                  ),
                );
                setGruposUsuariosSelecionados(() => {
                  return _internalRoles?.filter(item =>
                    response.grupo_usuarios.includes(Number(item.value)),
                  );
                });
                setListaArquivos(response.formattedFiles);
              })
              .finally(() => setIsFetching(false));
          }
        },
      );
    });
  }, []);

  const handleFileChange = (_file: File | null) => {
    setSelectedFile(_file);
    setNomeArquivo(_file ? _file.name : '');
  };

  const clearFile = () => {
    setSelectedFile(null);
    setNomeArquivo('');
  };

  const adicionarArquivoALista = () => {
    if (selectedFile) {
      setListaArquivos([
        ...listaArquivos,
        {
          id: uuid(),
          nome_arquivo: selectedFile.name,
          file: selectedFile,
          tamanho_arquivo: (selectedFile.size / (1024 * 1024))
            .toFixed(2)
            .replace('.', ','),
        } as AnnouncementsFile,
      ]);
      clearFile();
    }
  };

  const removerArquivoDaLista = (_id: string | number) => {
    setListaArquivos(
      listaArquivos.filter(item => item.id.toString() !== _id.toString()),
    );
  };

  const handleSelectedGroupsChange = async (itens?: SelectItem[]) => {
    setGruposSelecionados(itens || []);
  };

  const hadleSelectedUsersGroupsChange = async (itens?: SelectItem[]) => {
    setGruposUsuariosSelecionados(itens || []);
  };

  const validarCampos = () => {
    let isValid = true;
    if (!gruposSelecionados.length && !gruposUsuariosSelecionados.length) {
      toast({
        title:
          'Selecionar ao menos um grupo de clientes ou usuários é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    if (contarTotalTamanhoArquivos() > 5) {
      toast({
        title:
          'Tamanho acumulado da lista de arquivos não pode ser maior que 5MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    const auxMensagem = mensagem === '<p><br></p>' ? '' : mensagem;
    if (!auxMensagem) {
      toast({
        title: 'Campo mensagem é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    if (!titulo.trim()) {
      toast({
        title: 'Campo título é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    if (titulo.length > 255) {
      toast({
        title: 'Campo título deve conter no máximo 255 caracteres',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    return isValid;
  };

  const criarComunicado = (publicar = false) => {
    if (!validarCampos()) return;
    let request;
    if (id) {
      request = updateAnnouncement(comunicadoObj(publicar));
    } else {
      request = crateAnnouncement(comunicadoObj(publicar));
    }

    setIsFetching(true);
    request
      .then(response => {
        toast({
          title: `Comunicado ${
            publicar ? 'publicado' : id ? 'editado' : 'salvo'
          } com sucesso!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('announcements');
        if (publicar) history.push('/portal/admin/announcements');
        else if (id) voltar();
        else history.push('/portal/admin/announcements');
        onCloseModal();
      })
      .catch(err => {
        const errorsValidate = Object.entries(err.response.data);
        errorsValidate.forEach(_err => {
          toast({
            title: `Erro ao ${
              publicar ? 'publicar' : id ? 'editar' : 'salvar'
            } comunicado`,
            description: String(_err[1]),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        });
      })
      .finally(() => setIsFetching(false));
  };

  const comunicadoObj = (publicar = false) => {
    const auxMensagem = mensagem === '<p><br></p>' ? '' : mensagem;
    return {
      titulo,
      mensagem: auxMensagem,
      exigir_ciencia: exigirCiencia,
      alta_prioridade: altaPrioridade,
      grupo_clientes: gruposSelecionados?.map(it => it.value),
      grupo_usuarios: gruposUsuariosSelecionados?.map(it => it.value),
      arquivos: listaArquivos?.map(it => it.file || it.id),
      status: publicar ? 1 : 0,
      status_cliente: 0,
      id: id ? Number(id) : undefined,
    };
  };

  const voltar = () => {
    history.goBack();
  };

  const contarTotalTamanhoArquivos = () => {
    let total = 0;
    listaArquivos.forEach(item => {
      total += Number(item.tamanho_arquivo.toString().replace(',', '.'));
    });
    return total;
  };

  return (
    <Box px="8" py="4">
      <HStack gridGap={3}>
        <Heading>{id ? 'Editar comunicado' : 'Novo comunicado'}</Heading>
        {isFetching && <Spinner />}
      </HStack>
      <Stack py={8} w="70%" gridGap={4}>
        <Stack>
          <FormLabel margin={0} fontSize="14px">
            Título: <span style={{ color: 'red' }}>*</span>
          </FormLabel>
          <Input
            name="titulo"
            type="text"
            value={titulo}
            onChange={e => setTitulo(e.target.value)}
            fontSize="15px"
            isDisabled={isFetching}
          />
        </Stack>
        <Stack>
          <FormLabel margin={0} fontSize="14px">
            Mensagem: <span style={{ color: 'red' }}>*</span>
          </FormLabel>
          <TextEditor value={mensagem} onChange={setMensagem} />
        </Stack>
        <HStack alignItems="flex-end" gridGap={3} pt={2}>
          <FileUploader
            isRequired={false}
            isDisabled={isFetching}
            fileName={nomeArquivo}
            onChange={handleFileChange}
            clearFile={clearFile}
            label="Anexar arquivo:"
            showButton={false}
            helpText="Arquivo do tipo pdf. O limite total dos arquivos é de 5MB."
          />
          <Button
            variant="outline"
            w="10%"
            isDisabled={!selectedFile || isFetching}
            onClick={() => adicionarArquivoALista()}
          >
            Adicionar
          </Button>
        </HStack>
        <Table type="secondary">
          <Thead>
            <Tr>
              <Th p="0.5rem !important">NOME DO DOCUMENTO</Th>
              <Th p="0.5rem !important">TAMANHO</Th>
              <Th p="0.5rem !important" textAlign="center">
                AÇÃO
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {listaArquivos?.length ? (
              listaArquivos?.map(arquivo => (
                <Tr key={arquivo.id}>
                  <Td>{arquivo.nome_arquivo}</Td>
                  <Td>{arquivo.tamanho_arquivo} MB</Td>
                  <Td textAlign="center">
                    <Tooltip
                      hasArrow
                      label="Excluir"
                      bg="black"
                      shouldWrapChildren
                    >
                      <IconButton
                        onClick={() => removerArquivoDaLista(arquivo.id)}
                        aria-label="Excluir"
                        icon={<Icon as={FaTrash} />}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td textAlign="center" colSpan={3}>
                  Não há dados a serem apresentados
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
        <HStack gridGap={3}>
          <Stack w="50%">
            <FormLabel fontSize="14px" m={0}>
              Grupo de Clientes
            </FormLabel>
            <MultiSelect
              value={gruposSelecionados}
              options={groups}
              onChange={(changes: SelectItem[]) =>
                handleSelectedGroupsChange(changes)
              }
              disabled={isFetching}
              labelledBy="Selecione"
              overrideStrings={MultiSelectStrings}
            />
          </Stack>
          <Stack w="50%">
            <FormLabel fontSize="14px" m={0}>
              Grupo de usuários
            </FormLabel>
            <MultiSelect
              value={gruposUsuariosSelecionados}
              options={
                internalRoles && internalRoles?.length > 0 ? internalRoles : []
              }
              onChange={(changes: SelectItem[]) =>
                hadleSelectedUsersGroupsChange(changes)
              }
              disabled={isFetching}
              labelledBy="Selecione"
              overrideStrings={MultiSelectStrings}
            />
          </Stack>
        </HStack>
        <Stack gridGap={1}>
          <FormLabel fontSize="14px" m={0}>
            Configurações
          </FormLabel>
          <Checkbox
            colorScheme="b"
            isChecked={exigirCiencia}
            onChange={() => {
              setExigirCiencia(!exigirCiencia);
            }}
            isDisabled={isFetching}
            fontSize="sm"
          >
            Exigir ciência do cliente{' '}
            <b>(Li e estou de acordo com o comunicado)</b>.
          </Checkbox>
          <Checkbox
            colorScheme="b"
            isChecked={altaPrioridade}
            onChange={() => {
              setAltaPrioridade(!altaPrioridade);
            }}
            isDisabled={isFetching}
            fontSize="sm"
          >
            Publicar com <b>Alta prioridade</b>
          </Checkbox>
        </Stack>
        <HStack justifyContent="space-between" gridGap={3} mt={3}>
          <Button
            variant="outline"
            onClick={() => voltar()}
            isDisabled={isFetching}
          >
            Cancelar
          </Button>
          <HStack>
            <Button
              variant="outline"
              onClick={() => criarComunicado()}
              isDisabled={isFetching}
            >
              Salvar rascunho
            </Button>
            <Button
              colorScheme="b"
              onClick={() => {
                if (!validarCampos()) return;
                onOpenModal();
              }}
              isLoading={isFetching}
              isDisabled={isFetching}
            >
              Publicar
            </Button>
          </HStack>
        </HStack>
      </Stack>
      <ConfirmationDialog
        confirmar={() => criarComunicado(true)}
        onClose={onCloseModal}
        isOpen={isOpenModal}
        titulo="Publicar comunicado"
        btnConfirmText="Publicar"
        isDisabled={isFetching}
      >
        <span>
          Tem certeza que deseja publicar esse comunicado? Ao confirmar, esta
          ação não poderá ser desfeita.
        </span>
      </ConfirmationDialog>
    </Box>
  );
};
